import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { downloadImportTemplate, previewImportFromFile, previewImportFromUrl, rememberImportAliases, rememberRecentSheet } from '@/lib/api/procurementImport';
import { fetchVendors } from '@/lib/api/vendors';
import type { Vendor } from '@/types/api';
import type { ExistingItemRef, ImportPreview } from '@/types/procurementImport';
import { createRowStates, type ConfirmPlan, type ImportRowState } from './confirmPlan';
import { ReviewStep, type ConfirmMeta } from './ReviewStep';
import { SourceStep } from './SourceStep';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weekStartDate: string;
  weekLabel: string;
  /** Every item on this week's list, excluded ones included */
  existingItems: ExistingItemRef[];
  onConfirm: (plan: ConfirmPlan, meta: ConfirmMeta) => Promise<void>;
}

function rememberUrlSource(preview: ImportPreview) {
  if (preview.source?.kind !== 'url') return;
  const { sheetId, url, title } = preview.source;
  rememberRecentSheet({ sheetId, url, title }).catch((e) => console.warn('Could not remember this sheet', e));
}

/** Rows the caterer resolved to an ingredient the server had not matched confidently. */
export function pickedAliases(states: ImportRowState[]): { rawName: string; ingredientId: number }[] {
  return states
    .filter((s) => s.included && s.ingredientId !== null && !s.createIngredient && (s.row.matchStatus !== 'confident' || s.row.ingredient?.id !== s.ingredientId))
    .map((s) => ({ rawName: s.row.rawName, ingredientId: s.ingredientId! }));
}

function rememberPicks(states: ImportRowState[]) {
  const entries = pickedAliases(states);
  if (entries.length > 0) rememberImportAliases(entries).catch((e) => console.warn('Could not remember the ingredient picks', e));
}

type Source = { kind: 'file'; file: File } | { kind: 'url'; url: string };

export function ImportDialog({ open, onOpenChange, weekStartDate, weekLabel, existingItems, onConfirm }: ImportDialogProps) {
  const [source, setSource] = useState<Source | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [rowStates, setRowStates] = useState<ImportRowState[]>([]);
  const [busy, setBusy] = useState(false);
  const [readingTab, setReadingTab] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  // Bumped whenever the dialog closes, so a read or confirm that finishes afterwards is ignored.
  const sessionRef = useRef(0);

  // Failure just leaves the Vendor selects with Unassigned only.
  useEffect(() => {
    if (!open) return;
    fetchVendors({ isActive: true }).then(setVendors).catch((e) => console.warn('Could not load vendors', e));
  }, [open]);

  const read = useCallback(async (next: Source, tab?: string) => {
    const session = sessionRef.current;
    setBusy(true);
    setReadingTab(tab ?? null);
    setError(null);
    try {
      const result = next.kind === 'file'
        ? await previewImportFromFile(next.file, weekStartDate, tab)
        : await previewImportFromUrl(next.url, weekStartDate, tab);
      if (session !== sessionRef.current) return;
      rememberUrlSource(result);
      setSource(next);
      setPreview(result);
      setRowStates(createRowStates(result, existingItems));
    } catch (e) {
      if (session !== sessionRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to read the spreadsheet');
    } finally {
      setBusy(false);
      setReadingTab(null);
    }
  }, [existingItems, weekStartDate]);

  const handleRowChange = useCallback((key: string, patch: Partial<ImportRowState>) => {
    setRowStates((current) => current.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }, []);

  const handleConfirm = useCallback(async (plan: ConfirmPlan, meta: ConfirmMeta) => {
    const session = sessionRef.current;
    setConfirming(true);
    setError(null);
    try {
      await onConfirm(plan, meta);
      if (preview) rememberUrlSource(preview);
      rememberPicks(rowStates);
      if (session !== sessionRef.current) return;
      setPreview(null);
      setRowStates([]);
      setSource(null);
      onOpenChange(false);
    } catch (e) {
      if (session !== sessionRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to add items to this week');
    } finally {
      setConfirming(false);
    }
  }, [onConfirm, onOpenChange, preview, rowStates]);

  const handleOpenChange = (next: boolean) => {
    if (!next) { sessionRef.current += 1; setPreview(null); setRowStates([]); setSource(null); setError(null); }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${preview ? 'sm:max-w-4xl' : 'sm:max-w-xl'}`}>
        <DialogHeader>
          <DialogTitle>Import from sheet</DialogTitle>
          <DialogDescription>
            {preview ? 'Check each row, fix anything flagged, then add them to the week.' : 'Read a weekly grocery sheet and add its ingredients to this week\'s list.'}
          </DialogDescription>
        </DialogHeader>
        {preview ? (
          <>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <ReviewStep
              preview={preview}
              rowStates={rowStates}
              existingItems={existingItems}
              vendors={vendors}
              onRowChange={handleRowChange}
              onTabChange={(tab) => source && read(source, tab)}
              onConfirm={handleConfirm}
              confirming={confirming || busy}
              readingTab={readingTab}
            />
          </>
        ) : (
          <SourceStep
            weekLabel={weekLabel}
            busy={busy}
            error={error}
            onReadFile={(file) => read({ kind: 'file', file })}
            onReadUrl={(url) => read({ kind: 'url', url })}
            onDownloadTemplate={() => downloadImportTemplate(weekStartDate).catch((e) => setError(e instanceof Error ? e.message : 'Failed to download the template'))}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
